import { useState } from 'react';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { EditRounded as EditIcon } from '@material-ui/icons';

import { WeightUnity } from '../../types/weight-unity.type';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';

type WeightUnitiesTableHeader = {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
};

type Props = {
  weightUnities: WeightUnity[];
  isLoading: boolean;
  onEdit: (weightUnity: WeightUnity) => void;
};

const TABLE_HEADERS: WeightUnitiesTableHeader[] = [
  { key: 'name', label: 'Nom', minWidth: 200, align: 'left' },
  { key: 'abbreviation', label: 'Abréviation', minWidth: 120, align: 'left' },
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 90, align: 'right' },
];

const COLUMN_COUNT = TABLE_HEADERS.length;

const useStyles = makeStyles(() => ({
  headerCell: {
    fontWeight: 'bold',
  },
  emptyCell: {
    color: '#757575',
    textAlign: 'center',
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.25rem',
  },
}));

export function WeightUnitiesTable({ weightUnities, isLoading, onEdit }: Props) {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const lastPage = Math.max(0, Math.ceil(weightUnities.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);
  const visibleWeightUnities = weightUnities.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Paper>
      <TableContainer style={{ maxHeight: '65svh' }}>
        <Table size="small" stickyHeader aria-label="Liste des unités de poids">
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <TableCell
                  key={header.key}
                  aria-label={header.ariaLabel}
                  align={header.align}
                  className={classes.headerCell}
                  style={{ minWidth: header.minWidth }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={COLUMN_COUNT} />}
          {!isLoading && (
            <TableBody>
              {weightUnities.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className={classes.emptyCell}>
                    Aucune unité de poids : créez-en une depuis le formulaire ci-contre.
                  </TableCell>
                </TableRow>
              )}
              {visibleWeightUnities.map((weightUnity) => (
                <TableRow hover key={weightUnity.id}>
                  <TableCell>{weightUnity.name}</TableCell>
                  <TableCell>{weightUnity.abbreviation}</TableCell>
                  <TableCell align="right">
                    <div className={classes.rowActions}>
                      <IconButton size="small" aria-label="Modifier" onClick={() => onEdit(weightUnity)}>
                        <EditIcon />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={weightUnities.length}
        page={currentPage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => handleRowsPerPageChange(Number(event.target.value))}
      />
    </Paper>
  );
}
